import {store} from "./utils/Store";
import {WebHelpers} from "./utils/WebHelpers";
import {AppMessage, AppMessageType} from "./ui/AppMessage";
import * as misc from "./utils/misc";
import {Merchant} from "./Merchant";
import {Pay} from "./Pay";
import {Transfer} from "./Transfer";
import {Bridge} from "./Bridge";
import {logger} from "./utils/Log";
import {PaymentStatus} from "./structs/PaymentResponse";
import {Localizer} from "./Localizer";

export interface AppConfig {
    // PAY page
    bchdRestAddress: string;
    pathPay: string;
    pathTransfer: string;
    pathUrlShortener: string;
    apiBase: string;
    ref: string; // referer
    numDecimals: number;

    // on Pay (payment) page
    fullPage: boolean; // true = full page payment, false = iframe
    payment: {
        timeTotalSec: number;
        timeRemainingSec: number;
        maxRetryCount: number;
        id: string;
        status: PaymentStatus;
        address: string;
        address_slp: string;
        slp_token_id: string;
        // paidAmountCrypto: number;
        amount_crypto: number; // total amount
        // remainingAmountCrypto: number;
        checkMs: number;
        blockTime: number;
        returnUrl: string; // URL to send the customer back to. may be empty

        successPayImage: string; // image url, animated
        successPayImageFinal: string; // image url
    }

    transfer: {
        token: string;
        tx_id: string;
        checkMs: number;

        successPayImage: string;
        successPayImageFinal: string;
    }

    bridge: { // only defined before payment is made (1st page)
        maxAmount: number;
    }

    // Account page
    balance: number; // balance in merchant wallet

    // localizations
    tr: {
        // TODO can be removed in favor of new Localizer.ts
        close: string;
        confirmSignatures: string;
        confirmSlpTokens: string;
        confirmWalletBackup: string;
        withdrawalFailed: string;
        withdrawalSuccess: string;
        updating: string;
    }

    cookieLifeDays: number;
    cookiePath: string;
    cookieHost: string;
    siteUrl: string;
}

export type PageType = "landing" | "merchant" | "pay" | "transfer" | "bridge";

export class JqueryApp {
    protected static readonly TEXT_FIELDS = ["wp-address", "private-key", "token-id", "reward-amount",
        "wc-api-key", "wc-api-secret"];

    public readonly $: JQueryStatic;
    protected readonly config: AppConfig;
    protected readonly webHelpers: WebHelpers;
    protected readonly localizer: Localizer;
    protected readonly merchant: Merchant;
    protected readonly pay: Pay;
    protected readonly transfer: Transfer;
    protected readonly bridge: Bridge;

    protected readonly pageType: PageType;

    constructor($: JQueryStatic) {
        this.$ = $;
        this.config = (window as any).promptCfg || {};
        if (!this.config.cookieHost)
            this.config.cookieHost = window.location.host;

        this.webHelpers = new WebHelpers(window as any, this.$, this.config);
        const lang = this.webHelpers.getCookie("lng");
        this.localizer = new Localizer(this.webHelpers, lang, `${document.location.origin}/pub/locales/`);
        this.localizer.loadTranslationsFromObject("promptTr");
        this.localizer.exportTranslationFunctionToWindow();

        AppMessage.setJquery(this.$);
        AppMessage.setLocales(this.config.tr);

        // create the class and bind events based on the page we are on
        this.pageType = this.getPageType();
        logger.info("%s page loading", this.pageType);
        switch (this.pageType)
        {
            case "merchant":
                this.merchant = new Merchant(this);
                break;
            case "landing":
                break;
            case "pay":
                this.pay = new Pay(this);
                break;
            case "transfer":
                this.transfer = new Transfer(this);
                break;
            case "bridge":
                this.bridge = new Bridge(this);
                break;
            default:
                misc.assertUnreachableCode(this.pageType);
        }
        this.$(() => {
            this.setupBoostrapFramework();
        });
    }

    public getConfig() {
        return this.config;
    }

    public getWebHelpers() {
        return this.webHelpers;
    }

    public getTr() {
        return this.localizer;
    }

    // ################################################################
    // ###################### PRIVATE FUNCTIONS #######################

    protected getPageType(): PageType {
        // must be checked before landing-area
        if (this.$("body").hasClass("page-pay") === true)
            return "pay";
        else if (this.$("body").hasClass("page-transfer") === true)
            return "transfer";
        else if (this.$("body").hasClass("page-bridge") === true)
            return "bridge";
        else if (this.$("body").hasClass("landing-area") === true)
            return "landing";
        else if (this.$("body").hasClass("merchant-area") === true)
            return "merchant";
        throw new Error("Unknown page type on body: " + this.$("body").attr("class"));
    }

    protected setupBoostrapFramework() {
        const $ = this.$('[data-toggle="tooltip"]') as any;
        $.tooltip();
    }
}
